import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { OAuthToken } from './models/auth';
import { EnvironmentLoaderService } from './services/environment-loader.service';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { Router } from '@angular/router';
import { routes } from './app-routing.module';
import { environment } from 'src/environments/environment';


@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly ATTEMPTED_URL_KEY = 'attemptedUrl';
  private readonly DEFAULT_URL = '/';
  private token: OAuthToken | null = {};
  baseUrl: string;
  apiURL: string;

  constructor(
    private router: Router,
    private oauthService: OAuthService,
    private environmentLoaderService: EnvironmentLoaderService
  ) {
    this.apiURL = this.environmentLoaderService.config.siteBaseUrl;
    this.baseUrl = this.apiURL ? this.apiURL : window.location.origin;
  }

  get authToken(): OAuthToken | null {
    return this.token;
  }

  get username(): string {
    return this.oauthService.getIdentityClaims()?.['nickname'];
  }

  get roles(): any {
    return this.token?.realm_access?.roles;
  }

  get isSuperuser(): boolean {
    return this.hasRole('Egis_KS_Superbruger');
  }

  get isAdministrator(): boolean {
    return this.hasRole('egis_admin');
  }

  hasRole(allowedRoles: string): boolean {
    const rolesArray = allowedRoles.split(',');
    if (this.roles) {
      return this.roles.some((r: string) => rolesArray.includes(r));
    }
    return false;
  }

  public logout() {
    this.oauthService.logOut();
  }

  private get authConfiguration(): AuthConfig {
    const config = this.environmentLoaderService.config;

    return new AuthConfig({
      issuer: config.authIssuer,
      redirectUri: window.location.origin,
      clientId: config.clientId,
      responseType: 'code',
      scope: 'openid profile roles email offline_access',
      useSilentRefresh: false,
      timeoutFactor: 0.75,
      logoutUrl: config.authIssuer + 'oidc/logout',
      showDebugInformation: environment.production ? false : true,
      customQueryParams: {
        audience: 'egis-egis-service-external',
      },
    });
  }

  private redirectToAttemptedUrl() {
    const redirectUrl = sessionStorage.getItem(this.ATTEMPTED_URL_KEY) || sessionStorage.getItem(this.DEFAULT_URL);
    sessionStorage.removeItem(this.ATTEMPTED_URL_KEY);
    if (redirectUrl) {
      this.router.navigateByUrl(redirectUrl);
    }
  }

  private saveAttemptedUrl() {
    const attemptedPath = window.location.pathname;
    // Paths start with / but routes don't. This also makes sure our home url are never set, as it is also the default url.
    const cleanedPath = attemptedPath.startsWith(this.DEFAULT_URL) ? attemptedPath.slice(1) : attemptedPath;
    // Only save path if it is a valid route
    if (!!cleanedPath && routes.some((route) => route.path && cleanedPath.includes(route.path))) {
      sessionStorage.setItem(this.ATTEMPTED_URL_KEY, attemptedPath + window.location.search);
    }
  }

  /**
   * Initializes the authentication service, logging in the user if they are not already logged in
   * If its a public route, just let the user through
   * @returns true if the user is logged in, false otherwise
   */
  public async init(): Promise<boolean> {
    this.oauthService.configure(this.authConfiguration);
    this.oauthService.setupAutomaticSilentRefresh();
    this.oauthService.setStorage(localStorage);
    this.saveAttemptedUrl();

    return this.oauthService
      .loadDiscoveryDocumentAndLogin()
      .then((loggedIn) => {
        if (loggedIn) {
          const accessToken = this.oauthService.getAccessToken();
          this.token = this.parseToken(accessToken);

          this.redirectToAttemptedUrl();
        }
        return loggedIn;
      })
      .catch((error) => {
        console.error('AuthService.init() - error', error);
        return false;
      });
  }

  private parseToken(accessToken: string): OAuthToken | null {
    try {
      const payload = accessToken?.split('.')?.[1];
      if (payload) {
        return JSON.parse(atob(payload));
      } else {
        throw new Error('Invalid token format');
      }
    } catch (error) {
      console.error('Failed to parse token:', error);
      return null;
    }
  }
}
